import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Boxes,
    Wallet,
    CheckCircle2,
    TrendingUp,
    MapPin,
    Pencil,
    Pause,
    Play,
    Trash2,
    Plus,
    Sparkles,
    Loader2,
    PackageCheck,
    ShieldCheck,
    Gavel,
    Bell,
    Check,
    X as XIcon,
    Calendar as CalendarIcon,
    MessageSquare,
    ChevronDown,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { MY_LISTINGS, DELETE_DIALOG, RETURN_RENTAL, DEPOSIT, DISPUTE, RENTAL_REVIEW } from "../lib/testIds";
import NextStepDialog from "../components/NextStepDialog";
import {
    fetchMyListings,
    fetchMyAnalytics,
    updateListingStatus,
    deleteListing,
    fetchIncomingRequests,
    fetchRecentReturns,
    acceptRentalRequest,
    declineRentalRequest,
    acceptReturn,
    releaseDeposit,
} from "../lib/api";
import EditListingModal from "../components/EditListingModal";
import WithholdDepositModal from "../components/WithholdDepositModal";
import DepositDecisionModal from "../components/DepositDecisionModal";
import DisputeThreadModal from "../components/DisputeThreadModal";
import ReviewModal from "../components/ReviewModal";
import PayoutStatusBanner from "../components/PayoutStatusBanner";
import PayoutRequiredDialog, { detectPayoutRequired } from "../components/PayoutRequiredDialog";
import useDeepLinkedRental from "../hooks/useDeepLinkedRental";
import { useAuth } from "../context/AuthContext";

const STATUS_META = {
    active: {
        label: "Active",
        className: "bg-[#737a3d]/15 text-[#737a3d]",
    },
    rented: {
        label: "Rented",
        className: "bg-[#B56C5A]/15 text-[#B56C5A]",
    },
    inactive: {
        label: "Paused",
        className: "bg-[#2e3220]/10 text-[#2e3220]/80",
    },
};

const TABS = [
    { id: "all", label: "All listings", testId: MY_LISTINGS.tabAll },
    { id: "active", label: "Active", testId: MY_LISTINGS.tabActive },
    { id: "rented", label: "Rented", testId: MY_LISTINGS.tabRented },
    { id: "inactive", label: "Paused", testId: MY_LISTINGS.tabPaused },
];

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

const formatDateLabel = (iso) => {
    if (!iso) return "";
    try {
        const [y, m, d] = iso.split("T")[0].split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
        return iso;
    }
};

const KpiCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
    <div
        data-testid={testId}
        className={`rounded-3xl border border-[#2e3220]/10 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(46,50,32,0.08)] ${
            accent ? "bg-[#2e3220] text-[#F4F1EA]" : "bg-[#F4F1EA]"
        }`}
    >
        <div className="flex items-center justify-between">
            <p
                className={`text-[10px] font-bold uppercase tracking-widest ${
                    accent ? "text-[#8a9450]" : "text-[#737a3d]"
                }`}
            >
                {label}
            </p>
            <span
                className={`flex h-9 w-9 items-center justify-center rounded-2xl ${
                    accent ? "bg-[#F4F1EA]/10 text-[#8a9450]" : "bg-[#737a3d]/10 text-[#737a3d]"
                }`}
            >
                <Icon className="h-4 w-4" strokeWidth={2.25} />
            </span>
        </div>
        <p
            className={`mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl ${
                accent ? "text-[#F4F1EA]" : "text-[#2e3220]"
            }`}
        >
            {value}
        </p>
        {sub && (
            <p
                className={`mt-1 text-xs ${
                    accent ? "text-[#F4F1EA]/70" : "text-[#5a6142]"
                }`}
            >
                {sub}
            </p>
        )}
    </div>
);

const MyListings = () => {
    const [listings, setListings] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("all");
    // Owner explicitly wanted Active Listings to be the default landing tab.
    // Auto-flip is disabled — pending requests are still surfaced via the
    // dedicated toggle and the highlighted requests panel below.
    const [tabAutoFlipped, setTabAutoFlipped] = useState(true);

    // Deep-link: if the user arrived via a notification/email with
    // `?rental=<id>&tab=<name>`, switch tab and scroll+glow the card.
    useDeepLinkedRental({ setTab, loading });

    const [editTarget, setEditTarget] = useState(null);
    const [editOpen, setEditOpen] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [pausingId, setPausingId] = useState(null);

    // Incoming requests
    const [requests, setRequests] = useState([]);
    const [decidingId, setDecidingId] = useState(null);

    // Recent returns (renter marked items as returned)
    const [recentReturns, setRecentReturns] = useState([]);
    const [depositBusyId, setDepositBusyId] = useState(null);
    const [withholdTarget, setWithholdTarget] = useState(null);
    // After the owner accepts a return we automatically open the deposit
    // decision modal, then chain-open the review modal. This state drives
    // the auto-chain — see handleAcceptReturn → handleDepositDone.
    const [depositDecisionTarget, setDepositDecisionTarget] = useState(null);
    const [disputeTarget, setDisputeTarget] = useState(null);
    const [reviewTarget, setReviewTarget] = useState(null);
    const [acceptReturnBusyId, setAcceptReturnBusyId] = useState(null);
    // Big centered "next step" confirmation modal — shown after every
    // rental-lifecycle action so owners get clear directional guidance
    // (mirrors the "Request sent!" panel renters see on the browse modal).
    const [nextStep, setNextStep] = useState(null);
    // Earnings breakdown drawer — collapsible list of every finalized rental
    // and the payout amount the owner earned from it.
    const [earningsOpen, setEarningsOpen] = useState(false);
    // Stripe Connect gate dialog — auto-opens when accepting a request is
    // blocked by "owner hasn't finished payout onboarding".
    const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
    const { user } = useAuth();

    const loadAll = async () => {
        try {
            setLoading(true);
            const [l, a, r, rr] = await Promise.all([
                fetchMyListings(),
                fetchMyAnalytics(),
                fetchIncomingRequests().catch(() => ({ requests: [] })),
                fetchRecentReturns().catch(() => ({ returns: [] })),
            ]);
            setListings(l || []);
            setAnalytics(a || null);
            setRequests(r?.requests || []);
            setRecentReturns(rr?.returns || []);
        } catch (e) {
            toast.error("Couldn't load your dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const pendingRequests = useMemo(
        () => requests.filter((r) => r.status === "requested"),
        [requests],
    );

    // Auto-flip to the Pending Requests tab exactly once, on the first
    // load when there's something waiting. Manual tab clicks stick.
    useEffect(() => {
        if (!tabAutoFlipped && !loading && pendingRequests.length > 0) {
            setTab("pending-requests");
            setTabAutoFlipped(true);
        }
    }, [tabAutoFlipped, loading, pendingRequests.length]);

    // Split incoming returns into two piles: those still awaiting the owner's
    // "accept return" step (Pending Returns) and those fully finalized
    // (Previously Lended).
    const pendingReturns = useMemo(
        () => recentReturns.filter((r) => r.status === "return_pending"),
        [recentReturns],
    );
    const previouslyLended = useMemo(
        () => recentReturns.filter((r) => r.status !== "return_pending"),
        [recentReturns],
    );

    // Map item_id → the currently-active rental so each item card in the
    // grid can show a "Lent to <renter>" pin (mirrors the "Lent by
    // <lender>" pin on the Borrowing dashboard). Prefer the most recent
    // active rental if an item somehow has multiple.
    const activeRentalByItem = useMemo(() => {
        const map = {};
        for (const r of requests) {
            if (r.status !== "active" && r.status !== "return_pending") continue;
            const existing = map[r.item_id];
            if (!existing || (r.created_at || "") > (existing.created_at || "")) {
                map[r.item_id] = r;
            }
        }
        return map;
    }, [requests]);

    const handleAccept = async (rental) => {
        try {
            setDecidingId(rental.id);
            const updated = await acceptRentalRequest(rental.id);
            setRequests((rs) => rs.map((r) => (r.id === rental.id ? updated : r)));
            toast.success("Request accepted");
            setNextStep({
                title: "Request accepted!",
                description: `${rental.renter_name} has been notified to complete payment. Once they pay, this rental moves to 'Currently lending' and you'll get a notification with pickup coordination.`,
            });
        } catch (err) {
            const gate = detectPayoutRequired(err);
            if (gate === "owner") {
                setPayoutDialogOpen(true);
            } else {
                toast.error("Couldn't accept the request", {
                    description: err?.response?.data?.detail || "Try again in a moment.",
                });
            }
        } finally {
            setDecidingId(null);
        }
    };

    const handleDecline = async (rental) => {
        try {
            setDecidingId(rental.id);
            const updated = await declineRentalRequest(rental.id);
            setRequests((rs) => rs.map((r) => (r.id === rental.id ? updated : r)));
            toast.success("Request declined");
            setNextStep({
                title: "Request declined",
                description: `${rental.renter_name || "The renter"} has been notified. Feel free to send them a message if you'd like to suggest different dates or a different item.`,
            });
        } catch (err) {
            toast.error("Couldn't decline the request", {
                description: err?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setDecidingId(null);
        }
    };

    const handleReleaseDeposit = async (rental) => {
        try {
            setDepositBusyId(rental.id);
            const updated = await releaseDeposit(rental.id);
            setRecentReturns((rs) =>
                rs.map((r) => (r.id === rental.id ? updated : r)),
            );
            const refunded = !!updated.deposit_refund_id;
            toast.success("Deposit released");
            setNextStep({
                title: "Deposit released!",
                description: refunded
                    ? `${rental.renter_name || "The renter"} has been notified and the Stripe refund is on its way to their card (5–10 business days). This rental is now fully closed — leave a review from Recent returns.`
                    : `${rental.renter_name || "The renter"} has been notified. This rental is now fully closed and moves to your completed history.`,
            });
        } catch (err) {
            toast.error("Couldn't release the deposit", {
                description: err?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setDepositBusyId(null);
        }
    };

    const handleWithheld = (updated) => {
        setRecentReturns((rs) =>
            rs.map((r) => (r.id === updated.id ? updated : r)),
        );
        // After withholding is finalized, keep the auto-chain going and
        // prompt the review — same UX as clicking "Release" from the
        // decision modal. `updated` is the rental returned by the withhold
        // endpoint, so we can reuse it as the review target.
        setReviewTarget(updated);
    };

    const handleDisputeChanged = async () => {
        try {
            const rr = await fetchRecentReturns().catch(() => ({ returns: [] }));
            setRecentReturns(rr?.returns || []);
        } catch {
            /* ignore */
        }
    };

    const handleAcceptReturn = async (rental) => {
        try {
            setAcceptReturnBusyId(rental.id);
            const updated = await acceptReturn(rental.id);
            setRecentReturns((rs) =>
                rs.map((r) => (r.id === rental.id ? updated : r)),
            );
            toast.success("Return accepted");
            setNextStep({
                title: "Return accepted!",
                description: `${rental.renter_name || "Your renter"} has been notified. Your 90% cut is credited to your Lendly balance — tap Withdraw on the Lending banner to send it to your bank (connect Stripe first if you haven't). You can now leave a review from Recent returns.`,
            });
            // Deposit auto-chain has been disabled — jump straight to the
            // review prompt so the owner still gets a smooth close-out flow.
            setReviewTarget(updated);
        } catch (err) {
            toast.error("Couldn't accept the return", {
                description: err?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setAcceptReturnBusyId(null);
        }
    };

    const handleDepositDecisionClose = (rentalMaybe) => {
        const rental = rentalMaybe || depositDecisionTarget;
        setDepositDecisionTarget(null);
        // Move straight to leaving a review. If the caller passes null we
        // just close without prompting (shouldn't happen from the modal).
        if (rental) setReviewTarget(rental);
    };

    const handleDepositReleased = (updated) => {
        setRecentReturns((rs) =>
            rs.map((r) => (r.id === updated.id ? updated : r)),
        );
        handleDepositDecisionClose(updated);
    };

    const handleOpenWithholdFromDecision = (rental) => {
        // Hand off from the decision modal to the full withhold modal. Once
        // the withhold modal closes we'll still open the review below.
        setDepositDecisionTarget(null);
        setWithholdTarget(rental);
    };

    const handleReviewSubmitted = (review) => {
        if (!reviewTarget) return;
        setRecentReturns((rs) =>
            rs.map((r) =>
                r.id === reviewTarget.id ? { ...r, review } : r,
            ),
        );
    };

    const filtered = useMemo(() => {
        if (tab === "all") return listings;
        if (tab === "pending-returns") {
            // The pending-returns tab renders return rows, not listings.
            return [];
        }
        return listings.filter((l) => l.status === tab);
    }, [listings, tab]);

    const handleEdit = (item) => {
        setEditTarget(item);
        setEditOpen(true);
    };

    const handleSaved = (saved) => {
        setListings((rows) =>
            rows.map((r) => (r.id === saved.id ? { ...r, ...saved } : r)),
        );
    };

    const handleTogglePause = async (item) => {
        const wasPaused = item.status === "inactive";
        const next = wasPaused ? "active" : "inactive";
        try {
            setPausingId(item.id);
            const updated = await updateListingStatus(item.id, next);
            setListings((rows) =>
                rows.map((r) =>
                    r.id === item.id ? { ...r, status: updated.status, available: updated.available } : r,
                ),
            );
            // refresh analytics counts
            const a = await fetchMyAnalytics();
            setAnalytics(a);
            toast.success(wasPaused ? "Listing resumed" : "Listing paused", {
                description: wasPaused
                    ? `"${item.name}" is back on Browse and can receive new rental requests.`
                    : `"${item.name}" is hidden from Browse. Existing rentals continue — you can resume anytime.`,
                duration: 6000,
            });
        } catch (e) {
            toast.error("Couldn't update listing");
        } finally {
            setPausingId(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await deleteListing(deleteTarget.id);
            setListings((rows) => rows.filter((r) => r.id !== deleteTarget.id));
            const a = await fetchMyAnalytics();
            setAnalytics(a);
            toast.success("Listing deleted", {
                description: `"${deleteTarget.name}" has been removed from Browse. This can't be undone — you'll need to relist if you change your mind.`,
                duration: 6000,
            });
            setDeleteTarget(null);
        } catch (e) {
            toast.error("Couldn't delete listing");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <main
            data-testid={MY_LISTINGS.root}
            className="relative z-10 mx-auto max-w-7xl px-6 py-14 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Lender dashboard
                </span>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                        Lending
                    </h1>
                    <p className="mt-2 max-w-xl text-base leading-relaxed text-[#5a6142]">
                        Everything you&apos;ve put up to lend — earnings, status, and quick
                        actions to keep your gear paying for itself.
                    </p>
                </div>
                <Link
                    to="/list"
                    data-testid={MY_LISTINGS.listItemCta}
                    className="btn-primary self-start sm:self-auto"
                >
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                    List an item
                </Link>
            </div>

            {/* Filter toggles — each button determines which single section
                renders below. Previously-lended history now lives inside the
                collapsible "Your Lendly Earnings" card below. */}
            <PayoutStatusBanner />
            <section className="mt-6 grid gap-2 sm:gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <button
                    type="button"
                    data-testid={MY_LISTINGS.statListed}
                    onClick={() => setTab("all")}
                    aria-pressed={tab === "all"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        tab === "all"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${tab === "all" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        <Boxes className="h-3 w-3" strokeWidth={2.5} />
                        Active Listings
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : (analytics?.items_listed ?? 0)}
                    </p>
                    <p
                        className={`mt-1 text-xs ${tab === "all" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {analytics?.active_listings ?? 0} active ·{" "}
                        {analytics?.paused_listings ?? 0} paused
                    </p>
                </button>
                <button
                    type="button"
                    data-testid={MY_LISTINGS.statListed + "-active"}
                    onClick={() => setTab("rented")}
                    aria-pressed={tab === "rented"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        tab === "rented"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${tab === "rented" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        <PackageCheck className="h-3 w-3" strokeWidth={2.5} />
                        Currently Lending
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : (analytics?.rented_listings ?? 0)}
                    </p>
                    <p
                        className={`mt-1 text-xs ${tab === "rented" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {(analytics?.active_rentals ?? 0)} rental
                        {analytics?.active_rentals === 1 ? "" : "s"} in progress
                    </p>
                </button>
                <button
                    type="button"
                    data-testid="my-listings-stat-pending-requests"
                    onClick={() => setTab("pending-requests")}
                    aria-pressed={tab === "pending-requests"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        tab === "pending-requests"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${tab === "pending-requests" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        <Boxes className="h-3 w-3" strokeWidth={2.5} />
                        Pending Requests
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : pendingRequests.length}
                    </p>
                    <p
                        className={`mt-1 text-xs ${tab === "pending-requests" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {pendingRequests.length
                            ? "Awaiting your response"
                            : "Nothing waiting"}
                    </p>
                </button>
                <button
                    type="button"
                    data-testid="my-listings-stat-pending-returns"
                    onClick={() => setTab("pending-returns")}
                    aria-pressed={tab === "pending-returns"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        tab === "pending-returns"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${tab === "pending-returns" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        <PackageCheck className="h-3 w-3" strokeWidth={2.5} />
                        Pending Returns
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : pendingReturns.length}
                    </p>
                    <p
                        className={`mt-1 text-xs ${tab === "pending-returns" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {pendingReturns.length
                            ? "Awaiting your acceptance"
                            : "No returns to review"}
                    </p>
                </button>
            </section>

            {/* Incoming rental requests — surfaced on the Pending Requests
                filter (which auto-activates on load when there's action to
                take) as well as the default Currently Lending and Total
                Listings views. */}
            {pendingRequests.length > 0 && (tab === "pending-requests" || tab === "rented" || tab === "all") && (
                <section
                    data-testid="my-listings-incoming-requests"
                    className="mt-10 rounded-3xl border-2 border-[#737a3d]/30 bg-[#737a3d]/5 p-6"
                >
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                            <Bell className="h-5 w-5" strokeWidth={2.5} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <h2 className="font-display text-xl font-bold text-[#2e3220]">
                                Rental requests waiting on you
                            </h2>
                            <p className="mt-0.5 text-xs text-[#5a6142]">
                                Accept to send a payment link to the renter, or decline
                                if it doesn&apos;t work out.
                            </p>
                        </div>
                        <p className="rounded-full bg-[#737a3d] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA]">
                            {pendingRequests.length} pending
                        </p>
                    </div>
                    <ul className="mt-5 space-y-3">
                        {pendingRequests.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`incoming-request-${r.id}`}
                                data-rental-id={r.id}
                                className="rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4"
                            >
                                <div className="flex flex-wrap items-start gap-4">
                                    {r.item_image && (
                                        <img
                                            src={r.item_image}
                                            alt={r.item_name}
                                            className="h-16 w-16 flex-shrink-0 rounded-xl object-cover"
                                        />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-display text-base font-bold text-[#2e3220]">
                                            {r.renter_name}
                                            <span className="font-normal text-[#5a6142]">
                                                {" "}wants to rent{" "}
                                            </span>
                                            <span className="text-[#737a3d]">
                                                {r.item_name}
                                            </span>
                                        </p>
                                        <p className="mt-1 inline-flex items-center gap-1 text-xs text-[#5a6142]">
                                            <CalendarIcon className="h-3 w-3" />
                                            {formatDateLabel(r.start_date)} →{" "}
                                            {formatDateLabel(r.end_date)} ·{" "}
                                            {r.days} day{r.days > 1 ? "" : ""} ·{" "}
                                            <span className="font-bold text-[#2e3220]">
                                                {formatMoney(r.total_amount)}
                                            </span>
                                        </p>
                                        {r.renter_message && (
                                            <p className="mt-2 inline-flex items-start gap-1.5 rounded-2xl bg-[#2e3220]/5 px-3 py-1.5 text-xs italic text-[#5a6142]">
                                                <MessageSquare className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                                {r.renter_message}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-shrink-0 gap-2">
                                        <button
                                            type="button"
                                            data-testid={`incoming-accept-${r.id}`}
                                            disabled={decidingId === r.id}
                                            onClick={() => handleAccept(r)}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-[#737a3d] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#5f6534] disabled:opacity-50"
                                        >
                                            {decidingId === r.id ? (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : (
                                                <Check className="h-3 w-3" strokeWidth={2.5} />
                                            )}
                                            Accept
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`incoming-decline-${r.id}`}
                                            disabled={decidingId === r.id}
                                            onClick={() => handleDecline(r)}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-[#2e3220]/15 bg-transparent px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-50"
                                        >
                                            <XIcon className="h-3 w-3" strokeWidth={2.5} />
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Currently Lending / listings grid — only rendered when a
                listings-oriented filter is active (Currently Lending or
                Total Listings). Return-focused tabs render their own section
                below. */}
            {(tab === "rented" || tab === "all") && (
            <>
            <div className="mt-8 flex flex-wrap items-center justify-end gap-4">
                <div className="flex gap-1 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] p-1">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            data-testid={t.testId}
                            onClick={() => setTab(t.id)}
                            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                                tab === t.id
                                    ? "bg-[#737a3d] text-[#F4F1EA]"
                                    : "text-[#2e3220]/80 hover:text-[#737a3d]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div data-testid={MY_LISTINGS.loading} className="mt-6 space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-32 animate-pulse rounded-3xl bg-[#E8E2D0]"
                        />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div
                    data-testid={MY_LISTINGS.emptyState}
                    className="mt-10 rounded-3xl border border-dashed border-[#2e3220]/20 bg-[#F4F1EA] px-6 py-16 text-center"
                >
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                        <Boxes className="h-6 w-6" />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-[#2e3220]">
                        Nothing here yet
                    </h3>
                    <p className="mx-auto mt-2 max-w-sm text-sm text-[#5a6142]">
                        {tab === "all"
                            ? "List your first item to start earning from gear you already own."
                            : tab === "recently-returned"
                            ? "Nothing has been returned recently. Once a renter marks an item as returned, it shows up here."
                            : `No ${TABS.find((t) => t.id === tab)?.label.toLowerCase() || tab} listings in this view.`}
                    </p>
                </div>
            ) : (
                <ul
                    data-testid={MY_LISTINGS.list}
                    className="mt-6 space-y-4"
                >
                    {filtered.map((item) => {
                        const meta = STATUS_META[item.status] || STATUS_META.active;
                        const isPaused = item.status === "inactive";
                        return (
                            <li
                                key={item.id}
                                data-testid={MY_LISTINGS.row(item.id)}
                                className="overflow-hidden rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] transition-all duration-300 hover:shadow-[0_10px_30px_rgba(46,50,32,0.08)]"
                            >
                                <div className="grid gap-0 md:grid-cols-[180px_1fr_auto]">
                                    <div className="relative aspect-[4/3] md:aspect-auto md:h-full bg-[#E8E2D0]">
                                        <img
                                            src={item.image_url}
                                            alt={item.name}
                                            className="absolute inset-0 h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-3 px-5 py-5">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span
                                                data-testid={MY_LISTINGS.rowStatus(item.id)}
                                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${meta.className}`}
                                            >
                                                {meta.label}
                                            </span>
                                            <span className="rounded-full bg-[#2e3220]/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                {item.category}
                                            </span>
                                        </div>
                                        <div>
                                            <h3 className="font-display text-xl font-bold leading-tight text-[#2e3220]">
                                                {item.name}
                                            </h3>
                                            {item.location && (
                                                <p className="mt-1 inline-flex items-center gap-1 text-xs text-[#5a6142]">
                                                    <MapPin className="h-3.5 w-3.5" />
                                                    {item.location}
                                                </p>
                                            )}
                                            {activeRentalByItem[item.id] && (
                                                <p
                                                    data-testid={`listing-lent-to-${item.id}`}
                                                    className="mt-2 inline-flex items-center gap-1 text-xs text-[#5a6142]"
                                                >
                                                    <MapPin className="h-3.5 w-3.5" />
                                                    Lent to{" "}
                                                    {activeRentalByItem[item.id].renter_id ? (
                                                        <Link
                                                            to={`/neighbors/${activeRentalByItem[item.id].renter_id}`}
                                                            className="font-semibold text-[#737a3d] hover:text-[#2e3220] hover:underline"
                                                        >
                                                            {activeRentalByItem[item.id].renter_name}
                                                        </Link>
                                                    ) : (
                                                        activeRentalByItem[item.id].renter_name
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-6 text-sm">
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                    Price
                                                </p>
                                                <p className="font-display text-lg font-bold text-[#2e3220]">
                                                    {formatMoney(item.price_per_day)}
                                                    <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]/80">
                                                        /day
                                                    </span>
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                    Rentals
                                                </p>
                                                <p
                                                    data-testid={MY_LISTINGS.rowRentals(item.id)}
                                                    className="font-display text-lg font-bold text-[#2e3220]"
                                                >
                                                    {item.stats?.rentals_count ?? 0}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                    Earnings
                                                </p>
                                                <p
                                                    data-testid={MY_LISTINGS.rowRevenue(item.id)}
                                                    className="font-display text-lg font-bold text-[#737a3d]"
                                                >
                                                    {formatMoney(item.stats?.revenue ?? 0)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-row items-center justify-end gap-2 border-t border-[#2e3220]/10 px-5 py-4 md:flex-col md:items-stretch md:justify-center md:border-l md:border-t-0 md:px-5">
                                        <button
                                            type="button"
                                            onClick={() => handleEdit(item)}
                                            data-testid={MY_LISTINGS.rowEdit(item.id)}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#2e3220] px-4 py-2 text-xs font-bold text-[#F4F1EA] transition-colors hover:bg-[#3d4230]"
                                        >
                                            <Pencil className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleTogglePause(item)}
                                            disabled={pausingId === item.id}
                                            data-testid={MY_LISTINGS.rowPause(item.id)}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#2e3220]/20 bg-white/70 px-4 py-2 text-xs font-bold text-[#2e3220] transition-colors hover:bg-[#E8E2D0] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {pausingId === item.id ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : isPaused ? (
                                                <Play className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            ) : (
                                                <Pause className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            )}
                                            {isPaused ? "Resume" : "Pause"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget(item)}
                                            data-testid={MY_LISTINGS.rowDelete(item.id)}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#B56C5A]/40 bg-transparent px-4 py-2 text-xs font-bold text-[#B56C5A] transition-colors hover:bg-[#B56C5A]/10"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            {/* Earnings — collapsible. Click to expand into a per-rental
                breakdown of every finalized item the owner has lent out. */}
            <section className="mt-8">
                <button
                    type="button"
                    data-testid={MY_LISTINGS.headerEarnings}
                    onClick={() => setEarningsOpen((v) => !v)}
                    aria-expanded={earningsOpen}
                    aria-controls="lendly-earnings-breakdown"
                    className="group block w-full rounded-3xl border border-[#2e3220]/10 bg-[#2e3220] p-6 text-left text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(46,50,32,0.18)]"
                >
                    <div className="flex items-center justify-between">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#8a9450]">
                            Your Lendly Earnings
                        </p>
                        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#F4F1EA]/10 text-[#8a9450]">
                            <Wallet className="h-4 w-4" strokeWidth={2.25} />
                        </span>
                    </div>
                    <p className="mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl">
                        {loading ? "…" : formatMoney(analytics?.lifetime_earnings ?? 0)}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2 text-xs text-[#F4F1EA]/70">
                        <span>
                            {loading
                                ? "Crunching the numbers"
                                : `From ${analytics?.rentals_completed ?? 0} completed rental${analytics?.rentals_completed === 1 ? "" : "s"}`}
                        </span>
                        <span className="inline-flex items-center gap-1 font-bold uppercase tracking-widest text-[#8a9450]">
                            {earningsOpen ? "Hide breakdown" : "View breakdown"}
                            <ChevronDown
                                className={`h-3.5 w-3.5 transition-transform duration-200 ${earningsOpen ? "rotate-180" : ""}`}
                                strokeWidth={2.5}
                            />
                        </span>
                    </div>
                </button>
                {earningsOpen && (
                    <div
                        id="lendly-earnings-breakdown"
                        data-testid="my-listings-earnings-breakdown"
                        className="mt-3 overflow-hidden rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA]"
                    >
                        {previouslyLended.length === 0 ? (
                            <p
                                data-testid="my-listings-earnings-empty"
                                className="p-6 text-center text-sm text-[#5a6142]"
                            >
                                No completed rentals yet. Once you accept a return, it will show up here with the amount you earned.
                            </p>
                        ) : (
                            <ul className="divide-y divide-[#2e3220]/10">
                                {previouslyLended.map((r) => {
                                    // Owner take = payout_amount if set, else 90% of total (Lendly takes 10%).
                                    const payout = Number(r.payout_amount);
                                    const total = Number(r.total_amount) || 0;
                                    const earned = payout > 0 ? payout : total * 0.9;
                                    return (
                                        <li
                                            key={r.id}
                                            data-testid={`my-listings-earnings-row-${r.id}`}
                                            className="flex items-center gap-3 p-4"
                                        >
                                            <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-xl border border-[#2e3220]/10 bg-[#E8E2D0]">
                                                {r.item_image && (
                                                    <img
                                                        src={r.item_image}
                                                        alt={r.item_name}
                                                        className="h-full w-full object-cover"
                                                        loading="lazy"
                                                    />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate font-display text-sm font-bold text-[#2e3220]">
                                                    {r.item_name}
                                                </p>
                                                <p className="mt-0.5 truncate text-xs text-[#5a6142]">
                                                    {r.renter_name}
                                                    {r.start_date && (
                                                        <>
                                                            {" · "}
                                                            {formatDateLabel(r.start_date)} → {formatDateLabel(r.end_date)}
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0 text-right">
                                                <p className="font-display text-base font-bold text-[#737a3d]">
                                                    +{formatMoney(earned)}
                                                </p>
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-[#5a6142]/70">
                                                    Earned
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}
            </section>
            </>
            )}

            {/* Returns section — renders when the Pending Returns tab is
                active. Previously-lended history is surfaced via the
                collapsible "Your Lendly Earnings" card above. */}
            {tab === "pending-returns" && (() => {
                const rows = pendingReturns;
                const heading = "Pending Returns";
                const subtitle = "Renters have marked these as returned — accept to finalize.";
                const emptyMsg = "No returns waiting for your acceptance right now.";
                return (
                    <section
                        data-testid={RETURN_RENTAL.returnedSection}
                        className="mt-10 rounded-3xl border-2 border-[#5a6142]/20 bg-[#F4F1EA] p-6"
                    >
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#5a6142] text-[#F4F1EA]">
                                <PackageCheck className="h-5 w-5" strokeWidth={2.5} />
                            </span>
                            <div className="min-w-0 flex-1">
                                <h2 className="font-display text-xl font-bold text-[#2e3220]">
                                    {heading}
                                </h2>
                                <p className="mt-0.5 text-xs text-[#5a6142]">
                                    {subtitle}
                                </p>
                            </div>
                            <p className="rounded-full bg-[#5a6142] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA]">
                                {rows.length}
                            </p>
                        </div>
                        {rows.length === 0 ? (
                            <p className="mt-6 text-center text-sm text-[#5a6142]">
                                {emptyMsg}
                            </p>
                        ) : (
                        <ul className="mt-5 space-y-4">
                            {rows.map((r) => (
                            <li
                                key={r.id}
                                data-testid={RETURN_RENTAL.returnedRow(r.id)}
                                data-rental-id={r.id}
                                className="overflow-hidden rounded-2xl border border-[#2e3220]/10 bg-white/60"
                            >
                                <div className="grid gap-0 md:grid-cols-[auto_1fr]">
                                    <div className="flex gap-2 border-b border-[#2e3220]/10 p-3 md:border-b-0 md:border-r">
                                        <div className="flex flex-col items-center">
                                            <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                Before
                                            </p>
                                            <div className="h-20 w-20 overflow-hidden rounded-xl border border-[#2e3220]/10 bg-[#E8E2D0]">
                                                {r.item_image && (
                                                    <img
                                                        src={r.item_image}
                                                        alt={`${r.item_name} listing`}
                                                        className="h-full w-full object-cover"
                                                        loading="lazy"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-center">
                                            <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                After ({r.return_photos?.length || 0})
                                            </p>
                                            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-[#2e3220]/10 bg-[#E8E2D0]">
                                                {r.return_photos?.[0] ? (
                                                    <a
                                                        href={r.return_photos[0]}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block h-full w-full"
                                                    >
                                                        <img
                                                            src={r.return_photos[0]}
                                                            alt={`${r.item_name} returned`}
                                                            className="h-full w-full object-cover transition-transform hover:scale-105"
                                                            loading="lazy"
                                                        />
                                                    </a>
                                                ) : (
                                                    <span className="px-1 text-center text-[9px] text-[#5a6142]/60">
                                                        No photos
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <p className="font-display text-base font-bold text-[#2e3220]">
                                            {r.renter_name}
                                            <span className="font-normal text-[#5a6142]">
                                                {" "}returned{" "}
                                            </span>
                                            <span className="text-[#737a3d]">
                                                {r.item_name}
                                            </span>
                                        </p>
                                        <p className="mt-1 inline-flex items-center gap-1 text-xs text-[#5a6142]">
                                            <CalendarIcon className="h-3 w-3" />
                                            {formatDateLabel(r.start_date)} →{" "}
                                            {formatDateLabel(r.end_date)} ·{" "}
                                            {r.days} day{r.days > 1 ? "s" : ""}
                                        </p>
                                        {r.condition_note ? (
                                            <p className="mt-2 rounded-2xl bg-[#2e3220]/5 px-3 py-2 text-xs italic text-[#5a6142]">
                                                <MessageSquare className="mr-1 inline h-3 w-3" />
                                                “{r.condition_note}”
                                            </p>
                                        ) : (
                                            <p className="mt-2 text-xs text-[#5a6142]/60">
                                                No condition note left.
                                            </p>
                                        )}
                                        {r.return_photos?.length > 1 && (
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {r.return_photos.slice(1).map((url, i) => (
                                                    <a
                                                        key={`${r.id}-thumb-${i}`}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block h-12 w-12 overflow-hidden rounded-lg border border-[#2e3220]/10 bg-[#E8E2D0] transition-transform hover:scale-105"
                                                    >
                                                        <img
                                                            src={url}
                                                            alt={`Return ${i + 2}`}
                                                            className="h-full w-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    </a>
                                                ))}
                                            </div>
                                        )}

                                        {/* Accept return + Review renter CTAs */}
                                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#2e3220]/10 pt-3">
                                            {r.status === "return_pending" ? (
                                                <>
                                                    <span
                                                        data-testid={RETURN_RENTAL.pendingBadge(r.id)}
                                                        className="inline-flex items-center gap-1 rounded-full bg-[#d6a73f]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#7a5c1a]"
                                                    >
                                                        Awaiting your acceptance
                                                    </span>
                                                    <button
                                                        type="button"
                                                        data-testid={RETURN_RENTAL.acceptBtn(r.id)}
                                                        disabled={acceptReturnBusyId === r.id}
                                                        onClick={() => handleAcceptReturn(r)}
                                                        className="inline-flex items-center gap-1.5 rounded-full bg-[#737a3d] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#5f6534] disabled:opacity-50"
                                                    >
                                                        {acceptReturnBusyId === r.id ? (
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <Check className="h-3 w-3" strokeWidth={2.5} />
                                                        )}
                                                        Accept return
                                                    </button>
                                                </>
                                            ) : (
                                                // status === "completed" — offer review of the renter
                                                <>
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-[#5a6142]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#5a6142]">
                                                        <Check className="h-3 w-3" strokeWidth={2.5} />
                                                        Completed
                                                    </span>
                                                    {r.review ? (
                                                        <button
                                                            type="button"
                                                            data-testid={RENTAL_REVIEW.edit(r.id)}
                                                            onClick={() => setReviewTarget(r)}
                                                            className="inline-flex items-center gap-1 rounded-full bg-[#F4F1EA] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] hover:text-[#2e3220]"
                                                        >
                                                            Edit your review of {r.renter_name?.split(" ")[0] || "renter"}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            data-testid={RENTAL_REVIEW.cta(r.id)}
                                                            onClick={() => setReviewTarget(r)}
                                                            className="inline-flex items-center gap-1.5 rounded-full bg-[#2e3220] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#3d4230]"
                                                        >
                                                            Review {r.renter_name?.split(" ")[0] || "renter"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Deposit controls — hidden. All
                                            deposit / dispute UI is
                                            temporarily disabled across the
                                            app per product decision. Wrap
                                            in `false && ...` so the code is
                                            preserved for a quick re-enable
                                            later without a git revert. */}
                                        {false && (r.deposit || 0) > 0 && r.status === "completed" && (
                                            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#2e3220]/10 pt-3">
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                    Deposit · {formatMoney(r.deposit)}
                                                </span>
                                                {r.deposit_status === "released" ? (
                                                    <span
                                                        data-testid={DEPOSIT.statusBadge(r.id)}
                                                        className="inline-flex items-center gap-1 rounded-full bg-[#737a3d]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#5f6534]"
                                                    >
                                                        <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                                                        Released
                                                        {r.deposit_refund_id && (
                                                            <span className="font-normal text-[#5a6142]/80">
                                                                {" "}· Stripe refund issued
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : r.deposit_status === "withheld" ? (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span
                                                            data-testid={DEPOSIT.statusBadge(r.id)}
                                                            className="inline-flex items-center gap-1 rounded-full bg-[#B56C5A]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#7a3a30]"
                                                            title={r.deposit_withheld_reason || ""}
                                                        >
                                                            Withheld
                                                        </span>
                                                        {r.dispute_status && (
                                                            <button
                                                                type="button"
                                                                data-testid={DISPUTE.viewBtn(r.id)}
                                                                onClick={() => setDisputeTarget(r)}
                                                                className="inline-flex items-center gap-1.5 rounded-full bg-[#2e3220] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#3d4230]"
                                                            >
                                                                <Gavel className="h-3 w-3" strokeWidth={2.5} />
                                                                View dispute
                                                                <span
                                                                    data-testid={DISPUTE.badge(r.id)}
                                                                    className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] ${
                                                                        r.dispute_status === "open"
                                                                            ? "bg-[#B56C5A] text-[#F4F1EA]"
                                                                            : "bg-[#737a3d] text-[#F4F1EA]"
                                                                    }`}
                                                                >
                                                                    {r.dispute_status}
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <button
                                                            type="button"
                                                            data-testid={DEPOSIT.releaseBtn(r.id)}
                                                            disabled={depositBusyId === r.id}
                                                            onClick={() => handleReleaseDeposit(r)}
                                                            className="inline-flex items-center gap-1.5 rounded-full bg-[#737a3d] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#5f6534] disabled:opacity-50"
                                                        >
                                                            {depositBusyId === r.id ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                                                            )}
                                                            Release deposit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid={DEPOSIT.withholdBtn(r.id)}
                                                            disabled={depositBusyId === r.id}
                                                            onClick={() => setWithholdTarget(r)}
                                                            className="inline-flex items-center gap-1.5 rounded-full border border-[#B56C5A]/40 bg-transparent px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#B56C5A] hover:bg-[#B56C5A]/10 disabled:opacity-50"
                                                        >
                                                            Withhold
                                                        </button>
                                                    </div>
                                                )}
                                                {r.deposit_status === "withheld" && r.deposit_withheld_reason && (
                                                    <p className="mt-1 w-full rounded-2xl bg-[#B56C5A]/10 px-3 py-2 text-xs italic text-[#7a3a30]">
                                                        Reason: “{r.deposit_withheld_reason}”
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                            ))}
                        </ul>
                        )}
                    </section>
                );
            })()}

            {/* Edit modal */}
            <EditListingModal
                item={editTarget}
                open={editOpen}
                onOpenChange={(v) => {
                    setEditOpen(v);
                    if (!v) setEditTarget(null);
                }}
                onSaved={handleSaved}
            />

            {/* Delete confirm */}
            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(v) => {
                    if (!v) setDeleteTarget(null);
                }}
            >
                <AlertDialogContent
                    data-testid={DELETE_DIALOG.root}
                    className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220]"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl font-bold text-[#2e3220]">
                            Delete this listing?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-sm leading-relaxed text-[#5a6142]">
                            {deleteTarget
                                ? `"${deleteTarget.name}" will be removed from Lendly. Your past rental history is kept for your records.`
                                : ""}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel
                            data-testid={DELETE_DIALOG.cancel}
                            className="btn-secondary !py-2 text-sm"
                        >
                            Keep listing
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid={DELETE_DIALOG.confirm}
                            onClick={handleDelete}
                            disabled={deleting}
                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#B56C5A] px-5 py-2 text-sm font-bold text-[#F4F1EA] transition-colors hover:bg-[#a35d4d] disabled:opacity-60"
                        >
                            {deleting ? "Deleting…" : "Delete forever"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <WithholdDepositModal
                rental={withholdTarget}
                open={!!withholdTarget}
                onOpenChange={(o) => !o && setWithholdTarget(null)}
                onWithheld={handleWithheld}
            />

            <DepositDecisionModal
                rental={depositDecisionTarget}
                open={!!depositDecisionTarget}
                onOpenChange={(o) => !o && handleDepositDecisionClose()}
                onReleased={handleDepositReleased}
                onOpenWithhold={handleOpenWithholdFromDecision}
                onSkip={handleDepositDecisionClose}
            />

            <DisputeThreadModal
                rental={disputeTarget}
                currentUserId={user?.user_id}
                open={!!disputeTarget}
                onOpenChange={(o) => !o && setDisputeTarget(null)}
                onChanged={handleDisputeChanged}
            />

            <ReviewModal
                rental={reviewTarget}
                open={!!reviewTarget}
                onOpenChange={(o) => !o && setReviewTarget(null)}
                onSubmitted={handleReviewSubmitted}
                revieweeName={reviewTarget?.renter_name}
            />
            <PayoutRequiredDialog
                open={payoutDialogOpen}
                onOpenChange={setPayoutDialogOpen}
                variant="owner"
            />
            <NextStepDialog step={nextStep} onClose={() => setNextStep(null)} />
        </main>
    );
};

export default MyListings;
